import { useCallback, useEffect, useRef, useState } from 'react';

type AnyFunction<T extends any[] = any[], R = any> = (...args: T) => R;

export function useThrottle<T extends AnyFunction>(
  func: T,
  delay: number
): {
  throttledFunction: T;
  isThrottled: boolean;
  remainingTime: number;
} {
  const [isThrottled, setIsThrottled] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastArgsRef = useRef<T extends AnyFunction<infer A, any> ? A : never>();
  const lastThisRef = useRef<any>();

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isThrottled) {
      interval = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(interval!);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isThrottled]);

  const throttledFunction: T = useCallback(
    function (
      this: any,
      ...args: T extends AnyFunction<infer A, any> ? A : never
    ) {
      if (isThrottled) return;

      lastArgsRef.current = args;
      lastThisRef.current = this;

      if (!timeoutRef.current) {
        setIsThrottled(true);
        setRemainingTime(delay / 1000);
        try {
          const result = func.apply(lastThisRef.current, lastArgsRef.current!);
          lastArgsRef.current = undefined;
          lastThisRef.current = undefined;

          if (result && typeof (result as Promise<any>).then === 'function') {
            (result as Promise<any>).catch((error: any) => {
              console.error('Throttled function failed:', error);
              setIsThrottled(false);
              setRemainingTime(0);
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
            });
          }

          timeoutRef.current = setTimeout(() => {
            setIsThrottled(false);
            timeoutRef.current = null;
          }, delay);
        } catch (error) {
          console.error('Throttled function failed:', error);
          setIsThrottled(false);
          setRemainingTime(0);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }
      }
    },
    [func, delay, isThrottled]
  );

  return { throttledFunction, isThrottled, remainingTime };
}
